import React, { useCallback, useState } from 'react'

import type { UploadProps } from '@fourthwall/components'
import {
  Button,
  Fieldset,
  Input,
  InputUI,
  Loading,
  ModalBox,
  Select,
  Textarea,
  Toasts,
  Upload,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { RemovableUpload } from '@modules/Dashboard/Memberships/components'
import { useCreateImageResources } from '@modules/Dashboard/Memberships/hooks/useCreateImageResources'
import { getRejectionErrors } from '@utils/dropzone'

import { getCategorySelectOptions } from '../Podcast/utils'
import * as S from './EditPodcastModal.styled'
import type { FormValues } from './EditPodcastModal.types'
import { validationSchema } from './consts'

type Props = {
  title: string
  description: string
  authorName: string
  contactEmail: string
  artworkUrl: string
  category: string
  subcategory?: string
  loading: boolean
  onSubmit: (values: FormValues, newImageResourceId?: number) => Promise<void>
  onClose: () => void
}

export const EditPodcastModal = ({
  title,
  description,
  authorName,
  contactEmail,
  artworkUrl,
  category,
  subcategory,
  loading,
  onSubmit,
  onClose,
}: Props) => {
  const [newImageResourceId, setNewImageResourceId] = useState<number | undefined>(undefined)
  const { createImageResources, isLoading: isLoadingImageResources } = useCreateImageResources()

  const isLoading = loading || isLoadingImageResources

  const form = useFormik<FormValues>({
    initialValues: {
      title,
      description,
      authorName,
      contactEmail,
      artworkUrl,
      category: subcategory ? `${category} - ${subcategory}` : category,
    },
    onSubmit: (values) => {
      onSubmit(values, newImageResourceId)
    },
    validationSchema,
    validateOnMount: true,
  })

  const handleFileDropRejected: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles, {
      'file-too-large': 'File is larger than 10 megabytes',
    }).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const addArtwork = useCallback(
    async (files: File[]) => {
      createImageResources({
        files,
        onSuccess: (images) => {
          form.setFieldValue('artworkUrl', images[0].url)
          setNewImageResourceId(images[0].id)
        },
      })
    },
    [createImageResources, form],
  )

  const renderArtwork = () => {
    if (form.values.artworkUrl) {
      return (
        <S.ArtworkContainer>
          <S.UploadContainer>
            <RemovableUpload
              onRemove={() => {
                form.setFieldValue('artworkUrl', '')
                setNewImageResourceId(undefined)
              }}
            >
              <S.Artwork src={form.values.artworkUrl} />
            </RemovableUpload>
          </S.UploadContainer>
          <S.ArtworkInfo>
            <span>Cover artwork</span>
            <S.ArtworkRecommendation>Recommended 3000x3000px size</S.ArtworkRecommendation>
          </S.ArtworkInfo>
        </S.ArtworkContainer>
      )
    }

    return (
      <S.ArtworkContainer>
        <S.UploadContainer>
          <Loading isLoading={isLoading}>
            <Upload
              accept={{
                'image/*': [],
              }}
              maxSize={10485760}
              title="Drop image or browse"
              disabled={isLoading}
              onFileDrop={addArtwork}
              onFileDropRejected={handleFileDropRejected}
            />
          </Loading>
        </S.UploadContainer>
        <S.ArtworkInfo>
          <span>Cover artwork</span>
          <S.ArtworkRecommendation>Recommended 3000x3000px size</S.ArtworkRecommendation>
        </S.ArtworkInfo>
      </S.ArtworkContainer>
    )
  }

  return (
    <ModalBox
      title="Edit podcast details"
      buttons={[
        <Button label="Cancel" onClick={onClose} />,
        <Button
          label="Update"
          appearance="primary"
          loading={isLoading}
          onClick={form.submitForm}
        />,
      ]}
      onClose={onClose}
    >
      <S.Container>
        <Fieldset>
          <Fieldset.Row>
            <Fieldset.Item>{renderArtwork()}</Fieldset.Item>
          </Fieldset.Row>
          {form.errors.artworkUrl && <InputUI.Error>{form.errors.artworkUrl}</InputUI.Error>}
          <Fieldset.Row>
            <Fieldset.Item>
              <Input label="Podcast title" disabled={isLoading} {...form.getFieldProps('title')} />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Textarea
                label="Podcast description"
                placeholder="Podcast description"
                resize
                disabled={isLoading}
                {...form.getFieldProps('description')}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Input
                label="Author name"
                suggestion="This name will appear in the podcast players."
                disabled={isLoading}
                {...form.getFieldProps('authorName')}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Input
                label="Contact email"
                suggestion="Used to claim your podcast on Spotify. Not publicly shared"
                disabled={isLoading}
                {...form.getFieldProps('contactEmail')}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Select
                label="Category"
                disabled={isLoading}
                options={getCategorySelectOptions()}
                {...form.getFieldProps('category')}
              />
            </Fieldset.Item>
          </Fieldset.Row>
        </Fieldset>
      </S.Container>
    </ModalBox>
  )
}
